import { useRef, useState, useEffect } from 'react';
import type { Button as IcButton, ButtonMode, IntercomConfig } from '@livepuls/types';
import { Meter } from './Meter.js';
import { deriveLabel } from '../lib/labels.js';
import {
  subscribeExternalPort,
  unsubscribeExternalPort,
  engageExternalDevice,
  disengageExternalDevice,
  engageIntercom,
  disengageIntercom,
  engagePartyline,
  disengagePartyline,
  engageCallToAll,
  disengageCallToAll,
  engageListen,
  disengageListen,
  setExternalPortVolume,
  setExternalDeviceVolume,
  setIncomingPanelVolume,
  unlockAudio,
} from '../lib/livekit.js';
import { getSocket } from '../lib/socket.js';
import { api } from '../lib/api.js';
import { td } from '../lib/touch-debug.js';
import { HW_VOLUME_EVENT } from '../lib/hardware-ptt.js';

interface Props {
  button: IcButton;
  config: IntercomConfig;
  pressed: boolean;
  /** Per Admin-Remote aktiviert → Taste leuchtet wie gedrückt (rot). */
  remoteActive: boolean;
  /** Vom Admin (Remote) ferngesetzter Pegel (0–100); undefined = unverändert. */
  remoteVolume?: number | undefined;
  /** Wird gerade angesprochen → orange umrandet (kein „remote"-Text). */
  addressed?: boolean;
  /** Vom Hardware-Funktaster aktuell gewählte Taste → grün umrandet. */
  hwSelected?: boolean;
  /** Sortier-/Edit-Modus: Taste ist INERT (kein PTT), wird nur verschoben. */
  editing?: boolean;
  /** Kompakte Kästchen-Ansicht → kleinere Schrift/Meter. */
  compact?: boolean;
  panelId: string;
  onPressedChange: (pressed: boolean) => void;
}

const VOLUME_SWIPE_THRESHOLD = 10; // px horizontal bevor wir in den Volume-Modus wechseln
// PTT koppelt erst nach dieser kurzen Wartezeit ein. So lässt sich „sprechen"
// von „Lautstärke ziehen" unterscheiden: wischt man vorher quer, wird NICHT
// gesprochen, sondern nur die Lautstärke geregelt.
const PTT_ENGAGE_DELAY_MS = 130;

function modeOf(b: IcButton): ButtonMode | 'latching' {
  if ('mode' in b) return b.mode;
  return 'latching';
}

export function PanelButton({
  button,
  config,
  pressed,
  remoteActive,
  remoteVolume,
  addressed = false,
  hwSelected = false,
  editing = false,
  compact = false,
  panelId,
  onPressedChange,
}: Props): JSX.Element {
  const label = deriveLabel(button, config);
  const mode = modeOf(button);
  const isPTT = mode === 'ptt';

  // ----- Lautstärke (lokal, mit Optimismus + späterem API-PUT) -----
  const [volume, setVolume] = useState(button.volume);

  // Lautstärke des EIGENEN Mix-Verhältnisses laufend auf den passenden Kanal
  // anwenden (eingehendes Intercom/Listen-Audio des Ziel-Ports).
  useEffect(() => {
    if (button.type === 'intercom') {
      setIncomingPanelVolume(button.targetPanelId, volume);
    } else if (button.type === 'listen' && button.targetPanelId) {
      setIncomingPanelVolume(button.targetPanelId, volume);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [volume, button.type]);
  useEffect(() => {
    setVolume(button.volume);
  }, [button.volume]);
  // Admin hat den Pegel ferngesetzt → sofort übernehmen (Anzeige + Audio).
  useEffect(() => {
    if (remoteVolume === undefined) return;
    setVolume(remoteVolume);
    applyChannelVolume(remoteVolume);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remoteVolume]);

  // Optionaler Hardware-Funktaster (Laut/Leise): nudged NUR diese Taste, wenn
  // sie das Ziel ist. Ohne Funktaster feuert das Event nie → kein Effekt.
  const hwVolSaveTimer = useRef<number | undefined>(undefined);
  useEffect(() => {
    const onHwVol = (ev: Event): void => {
      const d = (ev as CustomEvent).detail as { buttonId?: string; delta?: number } | undefined;
      if (!d || d.buttonId !== button.id || typeof d.delta !== 'number') return;
      const next = Math.max(0, Math.min(100, live.current.volume + d.delta));
      setVolume(next);
      applyChannelVolume(next);
      // Speichern erst nach kurzer Ruhe (Laut/Leise wiederholt beim Halten).
      if (hwVolSaveTimer.current !== undefined) clearTimeout(hwVolSaveTimer.current);
      hwVolSaveTimer.current = window.setTimeout(() => {
        api.put(`/api/v1/buttons/${button.id}`, { volume: next }).catch((err) =>
          console.warn('[volume] hw save failed', err),
        );
      }, 400);
    };
    window.addEventListener(HW_VOLUME_EVENT, onHwVol as EventListener);
    return () => {
      window.removeEventListener(HW_VOLUME_EVENT, onHwVol as EventListener);
      if (hwVolSaveTimer.current !== undefined) clearTimeout(hwVolSaveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [button.id]);

  // ----- Geste (Tap / PTT / horizontaler Volume-Swipe) -----
  // WICHTIG für iOS: Touch wird über NATIVE Listener mit { passive:false }
  // verarbeitet, damit wir e.preventDefault() aufrufen können. Sonst frisst
  // Safari die horizontale Wischgeste (Edge-Swipe „Zurück" / Scroll) und der
  // Pegel lässt sich am Handy nicht regeln. Maus läuft über Pointer-Events.
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const gesture = useRef<{
    startX: number;
    baseVol: number;
    width: number;
    volMode: boolean;
    lastVol: number;
    active: boolean;
    /** PTT-Einkoppel-Timer (verzögertes Sprechen, s. PTT_ENGAGE_DELAY_MS). */
    pttTimer?: number | undefined;
    /** Wurde PTT bereits eingekoppelt (= es wird gesprochen)? */
    pttEngaged: boolean;
  } | null>(null);
  // Aktuelle Props/State für die EINMALIG gehängten Touch-Listener. Würde man
  // die Listener bei jedem Render neu hängen, bricht iOS die Geste mitten im
  // Wisch ab (Listener-Austausch = preventDefault verloren → Scroll/Abbruch).
  const live = useRef({ volume, isPTT, pressed, onPressedChange, button, editing });
  live.current = { volume, isPTT, pressed, onPressedChange, button, editing };

  // ----- Audio + Socket-Lifecycle ----
  // pressed ist die Ground-Truth für "lokal aktiv". Die Mikrofon-Aufnahme
  // besitzt allein die LiveKit-Engine (genau EIN getUserMedia) – hier wird
  // nur noch engaged/disengaged und das WS-Event gefeuert.
  useEffect(() => {
    if (pressed) {
      switch (button.type) {
        case 'external-port':
          void subscribeExternalPort({
            panelId,
            portKey: button.portKey,
            volume,
          }).catch((err) => console.warn('[livekit] subscribe port failed', err));
          break;
        case 'external-device': {
          const device = config.externalDevices?.find(
            (d) => d.id === button.externalDeviceId,
          );
          if (device) {
            void engageExternalDevice({
              panelId,
              buttonId: button.id,
              device,
              volume,
            }).catch((err) => console.warn('[livekit] engage device failed', err));
          } else {
            console.warn('[livekit] external device not found', button.externalDeviceId);
          }
          break;
        }
        case 'intercom':
          void engageIntercom({
            panelId,
            buttonId: button.id,
            targetPanelId: button.targetPanelId,
          }).catch((err) => console.warn('[livekit] engage intercom failed', err));
          break;
        case 'partyline': {
          // ext-mic-PortKeys der Geräte-Mitglieder dieser Partyline ermitteln.
          const pl = config.partylines.find((p) => p.id === button.partylineId);
          const deviceMicPortKeys = (pl?.deviceIds ?? [])
            .map((did) => config.externalDevices?.find((d) => d.id === did)?.micPortKey)
            .filter((k): k is string => !!k);
          void engagePartyline({
            panelId,
            buttonId: button.id,
            partylineId: button.partylineId,
            deviceMicPortKeys,
          }).catch((err) => console.warn('[livekit] engage partyline failed', err));
          break;
        }
        case 'call-to-all':
          void engageCallToAll({ panelId, buttonId: button.id }).catch((err) =>
            console.warn('[livekit] engage call-to-all failed', err),
          );
          break;
        case 'listen':
          void engageListen({
            panelId,
            buttonId: button.id,
            ...(button.targetPanelId ? { targetPanelId: button.targetPanelId } : {}),
            ...(button.partylineId ? { partylineId: button.partylineId } : {}),
          }).catch((err) => console.warn('[livekit] engage listen failed', err));
          break;
        default:
          break;
      }
      getSocket().emit('button:press', { panelId, buttonId: button.id });
      if (typeof navigator.vibrate === 'function') navigator.vibrate(40);
    } else {
      getSocket().emit('button:release', { panelId, buttonId: button.id });
      switch (button.type) {
        case 'external-port':
          void unsubscribeExternalPort(button.portKey).catch((err) =>
            console.warn('[livekit] unsubscribe port failed', err),
          );
          break;
        case 'external-device':
          void disengageExternalDevice(button.id).catch((err) =>
            console.warn('[livekit] disengage failed', err),
          );
          break;
        case 'intercom':
          void disengageIntercom(button.id).catch((err) =>
            console.warn('[livekit] disengage intercom failed', err),
          );
          break;
        case 'partyline':
          void disengagePartyline(button.id).catch((err) =>
            console.warn('[livekit] disengage partyline failed', err),
          );
          break;
        case 'call-to-all':
          void disengageCallToAll(button.id).catch((err) =>
            console.warn('[livekit] disengage call-to-all failed', err),
          );
          break;
        case 'listen':
          void disengageListen(button.id).catch((err) =>
            console.warn('[livekit] disengage listen failed', err),
          );
          break;
        default:
          break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pressed, button.type]);

  // ----- Handlers -----
  /** Lautstärke SOFORT auf den passenden Kanal anwenden (Echtzeit-Mix). */
  function applyChannelVolume(vol: number): void {
    const b = live.current.button;
    switch (b.type) {
      case 'external-port':
        setExternalPortVolume(b.portKey, vol);
        break;
      case 'external-device':
        setExternalDeviceVolume(b.id, vol);
        break;
      case 'intercom':
        setIncomingPanelVolume(b.targetPanelId, vol);
        break;
      case 'listen':
        if (b.targetPanelId) setIncomingPanelVolume(b.targetPanelId, vol);
        break;
      default:
        break;
    }
  }

  // --- Geteilte Gesten-Logik (Touch UND Maus rufen dieselben Schritte) ---
  // Liest IMMER aus live.current, damit die einmalig gehängten Touch-Listener
  // korrekt funktionieren (keine veralteten Closures).
  function beginGesture(clientX: number, el: HTMLButtonElement): void {
    unlockAudio();
    const base = live.current.volume;
    const g: NonNullable<typeof gesture.current> = {
      startX: clientX,
      baseVol: base,
      width: el.getBoundingClientRect().width || 1,
      volMode: false,
      lastVol: base,
      active: true,
      pttEngaged: false,
    };
    gesture.current = g;
    // PTT NICHT sofort einkoppeln, sondern erst nach kurzer Wartezeit – wenn bis
    // dahin nicht quer gewischt wurde (= Lautstärke-Geste). So wird beim
    // Lautstärke-Ziehen nicht versehentlich gesprochen.
    if (live.current.isPTT) {
      g.pttTimer = window.setTimeout(() => {
        const cur = gesture.current;
        if (cur && cur.active && !cur.volMode) {
          cur.pttEngaged = true;
          live.current.onPressedChange(true);
          if (typeof navigator.vibrate === 'function') navigator.vibrate(20);
        }
      }, PTT_ENGAGE_DELAY_MS);
    }
  }

  function moveGesture(clientX: number): void {
    const g = gesture.current;
    if (!g || !g.active) return;
    const dx = clientX - g.startX;
    // NUR horizontale Geste (links→rechts) regelt die Lautstärke.
    if (!g.volMode && Math.abs(dx) > VOLUME_SWIPE_THRESHOLD) {
      g.volMode = true;
      // Lautstärke-Geste erkannt → PTT-Einkoppel-Timer abbrechen und, falls
      // doch schon eingekoppelt, sofort wieder freigeben (kein Sprechen).
      if (g.pttTimer !== undefined) {
        clearTimeout(g.pttTimer);
        g.pttTimer = undefined;
      }
      if (live.current.isPTT && g.pttEngaged) {
        g.pttEngaged = false;
        live.current.onPressedChange(false);
      }
    }
    if (g.volMode) {
      // Ganze Tastenbreite L→R = volle Skala (0 → 100 %).
      const next = Math.max(0, Math.min(100, Math.round(g.baseVol + (dx / g.width) * 100)));
      g.lastVol = next;
      td.vol = next;
      setVolume(next);
      applyChannelVolume(next); // live hörbar
    }
  }

  function endGesture(): void {
    const g = gesture.current;
    if (!g || !g.active) return;
    g.active = false;
    gesture.current = null;
    // Laufenden PTT-Einkoppel-Timer immer abbrechen.
    if (g.pttTimer !== undefined) {
      clearTimeout(g.pttTimer);
      g.pttTimer = undefined;
    }
    if (g.volMode) {
      if (g.lastVol !== g.baseVol) {
        applyChannelVolume(g.lastVol);
        api.put(`/api/v1/buttons/${live.current.button.id}`, { volume: g.lastVol }).catch((err) => {
          console.warn('[volume] save failed', err);
          setVolume(g.baseVol);
          applyChannelVolume(g.baseVol);
        });
      }
      return; // war Lautstärke-Wisch → kein Toggle
    }
    if (live.current.isPTT) {
      // Nur freigeben, wenn tatsächlich eingekoppelt wurde (sonst war es ein
      // Tipp kürzer als die Wartezeit → es wurde gar nicht gesprochen).
      if (g.pttEngaged) live.current.onPressedChange(false);
      return;
    }
    live.current.onPressedChange(!live.current.pressed); // latching: umschalten
    if (typeof navigator.vibrate === 'function') navigator.vibrate(30);
  }

  // Native Touch-Listener (passive:false → preventDefault möglich). EINMALIG
  // gehängt (leere Deps), damit iOS die Geste nicht durch Listener-Austausch
  // mitten im Wisch abbricht. Aktuelle Werte kommen aus live.current.
  useEffect(() => {
    const el = btnRef.current;
    if (!el) return;
    const onTouchStart = (e: TouchEvent): void => {
      td.start++;
      td.note = `start n=${e.touches.length}`;
      // Sortier-/Edit-Modus: Taste ist inert, die Geste übernimmt der Container
      // (Drag&Drop). NICHT preventDefault → Event darf zum Wrapper hochblubbern.
      if (live.current.editing) return;
      if (e.touches.length !== 1) return;
      beginGesture(e.touches[0]!.clientX, el);
    };
    const onTouchMove = (e: TouchEvent): void => {
      td.move++;
      if (!gesture.current?.active) {
        td.note = 'move:no-active';
        return;
      }
      // Immer preventDefault aufrufen (auch wenn nicht cancelable) - verhindert,
      // dass iOS die Geste mit touchcancel abbricht. Das ist kritisch damit
      // horizontale Swipes für Lautstärke-Regelung auf dem Handy funktionieren.
      try {
        e.preventDefault();
      } catch {
        /* noop */
      }
      const cx = e.touches[0]!.clientX;
      td.lastDx = Math.round(cx - gesture.current.startX);
      moveGesture(cx);
      td.vm = gesture.current?.volMode ? 1 : 0;
    };
    const onTouchEnd = (): void => {
      td.end++;
      endGesture();
    };
    const onTouchCancel = (): void => {
      td.cancel++;
      td.note = 'CANCEL';
      endGesture();
    };
    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd);
    el.addEventListener('touchcancel', onTouchCancel);
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchCancel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Maus/Trackpad (Desktop) über Pointer-Events; Touch ignorieren (macht der
  // native Listener oben, sonst doppelt).
  function onPointerDown(e: React.PointerEvent<HTMLButtonElement>): void {
    td.ptr++;
    td.note = `ptr ${e.pointerType}`;
    if (live.current.editing) return; // Sortier-Modus → Drag übernimmt der Wrapper
    if (e.pointerType === 'touch') return;
    beginGesture(e.clientX, e.currentTarget as HTMLButtonElement);
    const move = (ev: PointerEvent): void => moveGesture(ev.clientX);
    const up = (): void => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      endGesture();
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
  }

  // Gedrückt ODER per Admin-Remote aktiv → rot. Nur „angesprochen" → orange.
  const active = pressed || remoteActive;

  return (
    <button
      ref={btnRef}
      type="button"
      onPointerDown={onPointerDown}
      onContextMenu={(e) => e.preventDefault()}
      className={[
        'pbtn h-full w-full',
        active && !editing ? 'pbtn--active' : '',
        addressed && !active && !editing ? 'pbtn--receiving animate-pulse-amber' : '',
        hwSelected && !editing ? 'pbtn--hw' : '',
        editing ? 'pbtn--editing' : '',
      ].join(' ')}
      aria-pressed={active}
      style={editing ? { touchAction: 'none' } : undefined}
    >
      <Meter level={volume / 100} dim segments={compact ? 10 : 16} />

      {editing ? (
        <span className="pointer-events-none absolute right-1 top-1 text-text-dim opacity-70">
          <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
            <circle cx="7" cy="5" r="1.4" /><circle cx="13" cy="5" r="1.4" />
            <circle cx="7" cy="10" r="1.4" /><circle cx="13" cy="10" r="1.4" />
            <circle cx="7" cy="15" r="1.4" /><circle cx="13" cy="15" r="1.4" />
          </svg>
        </span>
      ) : null}

      <div className="flex flex-col items-center justify-center flex-1 min-h-0 px-1">
        <span
          className={[
            'font-bold text-text leading-tight text-center break-words w-full',
            compact ? 'text-sm' : 'text-lg',
          ].join(' ')}
        >
          {label}
        </span>
      </div>
    </button>
  );
}
